import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, Tag, MapPin, TrendingUp, Box, BarChart3, Printer, History, PieChart, Inbox, Truck, FileText, ArrowRightLeft, Hash, MoveRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StagingAlert from '@/components/StagingAlert';
import { filterOperations } from '@/utils/operationAccess';
import { moduleReportTiles } from '@/lib/sectionModules';

const GoodsTransferDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/goods-transfer/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  // Common tiles for all sections (except Gate Entry)
  const commonTiles = [
    {
      id: 'inward-consignment',
      title: 'Inward Consignment',
      description: 'Receive and process incoming consignments',
      icon: Inbox,
      route: '/sections/goods-transfer/inward-consignment',
      color: 'bg-emerald-500'
    },
    {
      id: 'shipment-request',
      title: 'Shipment Request',
      description: 'View and process incoming shipment requests',
      icon: Truck,
      route: '/sections/goods-transfer/shipment-requests',
      color: 'bg-orange-500'
    },
    {
      id: 'internal-transfers',
      title: 'Internal Transfers',
      description: 'Handle transfers within/between sections',
      icon: ArrowRightLeft,
      route: '/sections/goods-transfer/internal-transfer',
      color: 'bg-indigo-500'
    },
    {
      id: 'daily-report',
      title: 'Day Wise Transaction Report',
      description: 'Daily summary report',
      icon: FileText,
      route: '/sections/goods-transfer/report',
      color: 'bg-slate-600'
    },
    {
      id: 'closing-stock',
      title: 'Closing Stock Report',
      description: 'Live closing stock by operation',
      icon: PieChart,
      route: '/sections/goods-transfer/closing-stock',
      color: 'bg-emerald-600'
    }
  ];

  const operations = [
    {
      id: 'bundle-receipt',
      title: 'Bundle Receipt Entry',
      description: 'Receive and verify incoming bundles',
      icon: Inbox,
      route: '/sections/goods-transfer/bundle-receipt',
      color: 'bg-emerald-500'
    },
    {
      id: 'packing',
      title: 'Packing',
      description: 'Pack USNs into bundles for transfer',
      icon: Package,
      route: '/sections/goods-transfer/packing',
      color: 'bg-blue-500'
    },
    {
      id: 'sort-no-assignment',
      title: 'Sort No Assignment',
      description: 'Assign Sort No & Color to packed bundles',
      icon: Hash,
      route: '/sections/goods-transfer/sort-no-assignment',
      color: 'bg-cyan-500'
    },
    {
      id: 'fg-assignment',
      title: 'FG Assignment',
      description: 'Assign Finished Goods (FG) to bundles',
      icon: Tag,
      route: '/sections/goods-transfer/fg-assignment',
      color: 'bg-green-500'
    },
    {
      id: 'location-assignment',
      title: 'Location Assignment',
      description: 'Assign storage location to received bundles',
      icon: MapPin,
      route: '/sections/goods-transfer/location-assignment',
      color: 'bg-purple-500'
    },
    {
      id: 'location-transfer',
      title: 'Location Transfer',
      description: 'Move bundles between locations',
      icon: MoveRight,
      route: '/sections/goods-transfer/location-transfer',
      color: 'bg-violet-500'
    },
    {
      id: 'main-label-printing',
      title: 'Main Label Printing',
      description: 'Generate product labels with QR codes',
      icon: Printer,
      route: '/sections/goods-transfer/main-label-printing',
      color: 'bg-orange-500'
    },
    {
      id: 'print-job-history',
      title: 'Print Job History',
      description: 'View and track label printing records',
      icon: History,
      route: '/sections/goods-transfer/print-job-history',
      color: 'bg-slate-500'
    },
    {
      id: 'label-reports',
      title: 'Label Reports',
      description: 'Design-wise analysis and USN lookup',
      icon: BarChart3,
      route: '/sections/goods-transfer/label-reports',
      color: 'bg-teal-500'
    }
  ];

  // 2026-05-20: filter tiles by the user's granted operations in the
  // `goods_transfer` module (e.g., only show Packing + FG Assignment if
  // those are the only two ops granted in User Management).  Admins
  // and users with no allowed_operations grants still see everything
  // (filterOperations short-circuits on Admin/empty-config).
  const visibleOperations = filterOperations('goods_transfer', [...operations, ...moduleReportTiles('goods-transfer')]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Goods Transfer Module</h1>
              <p className="text-sm text-[#9CA3AF]">Pack, Assign FG & Manage Locations</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Staging Alert */}
        <StagingAlert 
          section="goods-transfer" 
          onNavigate={() => navigate('/sections/goods-transfer/inward-consignment')}
        />

        {/* Common Section Tiles */}
        <div>
          <h2 className="text-2xl font-bold text-[#1A1F2E] mb-2 font-display">
            Section Management
          </h2>
          <p className="text-[#5A5F6E] mb-4">Consignments, Transfers & Reports</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {commonTiles.map((tile) => {
            const Icon = tile.icon;
            return (
              <div
                key={tile.id}
                onClick={() => navigate(tile.route)}
                className="bg-white rounded-lg border-2 border-[#E5DFD0] hover:border-[#2D5F4F] hover:shadow-md cursor-pointer transition-all duration-200 p-4"
                data-testid={`common-tile-${tile.id}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 rounded-lg ${tile.color} flex items-center justify-center mb-3`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-sm font-semibold text-[#1A1F2E] mb-1 font-display">
                    {tile.title}
                  </h3>
                  <p className="text-xs text-[#5A5F6E]">{tile.description}</p>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Box className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {loading ? '...' : stats?.packing?.total_usns_packed || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">Total USNs Packed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {loading ? '...' : stats?.bundles?.total || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">Total Bundles</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Tag className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {loading ? '...' : stats?.bundles?.fg_assigned || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">FG Assigned</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <BarChart3 className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1F2E]">
                    {loading ? '...' : stats?.item_master_count || 0}
                  </p>
                  <p className="text-xs text-[#5A5F6E]">Item Master</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Operations */}
        <div>
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4">Operations</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {visibleOperations.map((op) => (
              <Card 
                key={op.id}
                className="bg-white border-none shadow-md hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => navigate(op.route)}
                data-testid={op.testId || `${op.id}-card`}
              >
                <CardHeader className="pb-3">
                  <div className={`w-12 h-12 ${op.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                    <op.icon className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg text-[#1A1F2E]">{op.title}</CardTitle>
                  <CardDescription className="text-[#5A5F6E]">{op.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>

        {/* Today's Activity */}
        <Card className="bg-white border-none shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-orange-500" />
              Today's Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-bold text-blue-600">
                  {loading ? '...' : stats?.packing?.packed_today || 0}
                </p>
                <p className="text-sm text-[#5A5F6E]">USNs Packed Today</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-bold text-green-600">
                  {loading ? '...' : stats?.bundles?.fg_assigned || 0}
                </p>
                <p className="text-sm text-[#5A5F6E]">FG Assigned</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-2xl font-bold text-purple-600">
                  {loading ? '...' : stats?.bundles?.location_assigned || 0}
                </p>
                <p className="text-sm text-[#5A5F6E]">Location Assigned</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default GoodsTransferDashboard;
